import React from 'react';
import { Box, Chip, Grid, Typography, makeStyles } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import LanguageIcon from '@material-ui/icons/Language';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/Phone';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import DescriptionIcon from '@material-ui/icons/Description';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';
import { fontFamilies } from '../../theme/fonts';

import { colors } from '../../theme/tokens';
const CORAL = colors.progressFill;
const DARK = colors.textPrimary;

/**
 * The blueprint exactly as an employer sees it.
 *
 * Shared by the public page, the student's own preview and the staff
 * preview, so "preview" is never a near-copy that drifts from the real
 * thing — all three render this component.
 */
const useStyles = makeStyles((theme) => ({
  // Printing this component is how "download" works, so the rules live
  // with it — that way the student preview, the staff preview and the
  // public page all print identically. Two things must be forced: colour
  // adjust (browsers drop backgrounds and gradients when printing) and
  // unclipping the dialog, whose content scrolls on screen so only the
  // visible part would otherwise reach the page.
  '@global': {
    '@media print': {
      '@page': { margin: '10mm' },
      'html, body': {
        background: `${colors.bgSurface} !important`,
        height: 'auto !important',
        overflow: 'visible !important',
      },
      'body *': { visibility: 'hidden' },
      '#blueprint-print-area, #blueprint-print-area *': {
        visibility: 'visible',
        WebkitPrintColorAdjust: 'exact !important',
        printColorAdjust: 'exact !important',
        colorAdjust: 'exact !important',
      },
      '#blueprint-print-area': {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        maxWidth: '100%',
        margin: 0,
        padding: 0,
      },
      '#blueprint-print-area a, #blueprint-print-area .MuiGrid-item': {
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      },
      '.MuiDialog-root': {
        position: 'absolute !important' as any,
        overflow: 'visible !important',
      },
      '.MuiDialog-container': {
        display: 'block !important',
        height: 'auto !important',
      },
      '.MuiDialog-paper': {
        boxShadow: 'none !important',
        margin: '0 !important',
        maxWidth: '100% !important',
        maxHeight: 'none !important',
        width: '100% !important',
        overflow: 'visible !important',
        background: `${colors.bgSurface} !important`,
      },
      '.MuiDialogContent-root': {
        overflow: 'visible !important',
        maxHeight: 'none !important',
        padding: '0 !important',
        border: 'none !important',
        background: `${colors.bgSurface} !important`,
      },
      '.MuiDialogTitle-root, .MuiDialogActions-root, .MuiBackdrop-root': {
        display: 'none !important',
      },
      '.no-print': { display: 'none !important' },
    },
  },
  shell: { maxWidth: 900, margin: '0 auto' },
  header: {
    background: `linear-gradient(120deg, ${CORAL} 0%, ${colors.accentOrange} 100%)`,
    borderRadius: 16,
    padding: '30px 34px',
    color: colors.onStat,
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    [theme.breakpoints.down('xs')]: {
      padding: '20px 16px',
      flexDirection: 'column',
      textAlign: 'center',
      gap: 14,
    },
  },
  // Fixed square, cropped — so a portrait, a landscape and a huge photo
  // all present identically instead of stretching the header.
  avatar: {
    width: 104,
    height: 104,
    minWidth: 104,
    borderRadius: '50%',
    objectFit: 'cover',
    objectPosition: 'center',
    border: '3px solid rgba(255,255,255,0.65)',
    background: 'rgba(255,255,255,0.18)',
  },
  name: {
    fontFamily: fontFamilies.display,
    fontSize: 21,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  headline: { fontSize: 15, color: colors.onStat, marginTop: 3 },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginTop: 9,
    flexWrap: 'wrap',
    fontSize: 13,
    color: colors.onStat,
    [theme.breakpoints.down('xs')]: { justifyContent: 'center' },
  },
  card: {
    background: colors.bgSurface,
    borderRadius: 14,
    border: `1px solid ${colors.border}`,
    padding: '20px 24px',
    marginTop: 16,
    [theme.breakpoints.down('xs')]: { padding: '16px 14px' },
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    marginBottom: 12,
  },
  bio: { fontSize: 14.5, color: colors.textStrong, lineHeight: 1.65 },
  skill: {
    background: colors.tintRed,
    color: CORAL,
    fontWeight: 600,
    fontSize: 12.5,
    marginRight: 6,
    marginBottom: 6,
  },
  workCard: {
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    textDecoration: 'none',
    background: colors.bgSurface,
    transition: 'box-shadow 0.15s, transform 0.15s',
    '&:hover': {
      boxShadow: `0 6px 20px ${colors.shadow}`,
      transform: 'translateY(-2px)',
    },
  },
  // Every cover sits in the same 16:9 box and is cropped to fill it, so a
  // grid of mixed-size images still lines up.
  workImgWrap: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    background: colors.bgRaised,
    overflow: 'hidden',
  },
  workImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
  },
  workImgEmpty: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.textMuted,
  },
  workBody: { padding: '12px 14px', flex: 1 },
  workTitle: {
    fontSize: 14.5,
    fontWeight: 700,
    color: DARK,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  workDesc: { fontSize: 12.5, color: colors.textMuted, marginTop: 4 },
  docRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    textDecoration: 'none',
    marginBottom: 8,
    '&:hover': { background: colors.bgSubtle },
  },
  docTitle: { fontSize: 13.5, fontWeight: 600, color: DARK },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    fontSize: 13.5,
    color: colors.textStrong,
    padding: '6px 0',
    textDecoration: 'none',
  },
  footer: {
    textAlign: 'center',
    fontSize: 11.5,
    color: colors.textMuted,
    marginTop: 22,
    paddingBottom: 10,
  },
}));

const hideBroken = (e: React.SyntheticEvent<HTMLImageElement>) => {
  (e.target as HTMLImageElement).style.display = 'none';
};

export interface BlueprintViewProps {
  bp: any;
}

const BlueprintView: React.FC<BlueprintViewProps> = ({ bp }) => {
  const classes = useStyles();
  if (!bp) return null;

  const items = bp.items ?? [];
  const work = items.filter((i: any) => i.category !== 'document');
  const docs = items.filter((i: any) => i.category === 'document');
  const skills: string[] =
    bp.skillList ??
    String(bp.skills ?? '')
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean);

  const coreDocs = [
    bp.cvUrl && { title: 'Curriculum Vitae (CV)', link: bp.cvUrl },
    bp.coverLetterUrl && { title: 'Cover letter', link: bp.coverLetterUrl },
    ...docs.map((d: any) => ({ title: d.title, link: d.link })),
  ].filter(Boolean) as { title: string; link: string }[];

  const contacts = [
    { key: 'publicEmail', Icon: EmailIcon, href: `mailto:${bp.publicEmail}` },
    { key: 'phone', Icon: PhoneIcon, href: `tel:${bp.phone}` },
    { key: 'website', Icon: LanguageIcon, href: bp.website },
    { key: 'linkedin', Icon: LanguageIcon, href: bp.linkedin },
    { key: 'github', Icon: LanguageIcon, href: bp.github },
    { key: 'behance', Icon: LanguageIcon, href: bp.behance },
    { key: 'instagram', Icon: LanguageIcon, href: bp.instagram },
  ].filter((c) => !!bp[c.key]);

  return (
    <div className={classes.shell} id="blueprint-print-area">
      <div className={classes.header}>
        {bp.avatarUrl ? (
          <img
            src={bp.avatarUrl}
            alt={bp.studentName}
            className={classes.avatar}
            onError={hideBroken}
          />
        ) : (
          <div className={classes.avatar} />
        )}
        <div>
          <div className={classes.name}>{bp.studentName}</div>
          {bp.headline && <div className={classes.headline}>{bp.headline}</div>}
          <div className={classes.metaRow}>
            {bp.location && (
              <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                <LocationOnIcon style={{ fontSize: 15 }} />
                {bp.location}
              </Box>
            )}
            {bp.availability && (
              <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                <WorkOutlineIcon style={{ fontSize: 15 }} />
                {bp.availability}
              </Box>
            )}
            {bp.languages && <span>{bp.languages}</span>}
          </div>
        </div>
      </div>

      {bp.bio && (
        <div className={classes.card}>
          <div className={classes.sectionTitle}>About</div>
          <div className={classes.bio}>{bp.bio}</div>
          {bp.preferredRole && (
            <Typography
              style={{
                fontSize: 13,
                color: colors.textMuted,
                marginTop: 10,
              }}
            >
              Looking for: <strong>{bp.preferredRole}</strong>
            </Typography>
          )}
        </div>
      )}

      {skills.length > 0 && (
        <div className={classes.card}>
          <div className={classes.sectionTitle}>Key skills</div>
          <Box display="flex" flexWrap="wrap">
            {skills.map((s) => (
              <Chip key={s} label={s} size="small" className={classes.skill} />
            ))}
          </Box>
        </div>
      )}

      {work.length > 0 && (
        <div className={classes.card}>
          <div className={classes.sectionTitle}>Work</div>
          <Grid container spacing={2}>
            {work.map((it: any) => (
              <Grid item xs={12} sm={6} md={4} key={it.id}>
                <a
                  href={it.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.workCard}
                >
                  <div className={classes.workImgWrap}>
                    {it.imageUrl ? (
                      <img
                        src={it.imageUrl}
                        alt=""
                        className={classes.workImg}
                        onError={hideBroken}
                      />
                    ) : (
                      <div className={classes.workImgEmpty}>
                        <WorkOutlineIcon style={{ fontSize: 28 }} />
                      </div>
                    )}
                  </div>
                  <div className={classes.workBody}>
                    <div className={classes.workTitle}>
                      {it.title}
                      <OpenInNewIcon
                        style={{ fontSize: 13, color: colors.textMuted }}
                      />
                    </div>
                    {it.description && (
                      <div className={classes.workDesc}>{it.description}</div>
                    )}
                  </div>
                </a>
              </Grid>
            ))}
          </Grid>
        </div>
      )}

      {coreDocs.length > 0 && (
        <div className={classes.card}>
          <div className={classes.sectionTitle}>CV &amp; core documents</div>
          {coreDocs.map((d) => (
            <a
              key={d.title + d.link}
              href={d.link}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.docRow}
            >
              <DescriptionIcon style={{ fontSize: 19, color: CORAL }} />
              <span className={classes.docTitle} style={{ flex: 1 }}>
                {d.title}
              </span>
              <OpenInNewIcon
                style={{ fontSize: 15, color: colors.textMuted }}
              />
            </a>
          ))}
        </div>
      )}

      {contacts.length > 0 && (
        <div className={classes.card}>
          <div className={classes.sectionTitle}>Get in touch</div>
          {contacts.map(({ key, Icon, href }) => (
            <a
              key={key}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.contactRow}
            >
              <Icon style={{ fontSize: 16, color: CORAL }} />
              <span style={{ wordBreak: 'break-all' }}>{bp[key]}</span>
            </a>
          ))}
        </div>
      )}

      <div className={classes.footer}>Built with Elevate Academy</div>
    </div>
  );
};

export default BlueprintView;
